import { useRequiredContext } from "@redotech/react-util/context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { OrderManagementPermissions } from "@redotech/redo-model/permissions";
import { breadcrumbSlot } from "@redotech/redo-web/breadcrumb";
import { memo, ReactNode } from "react";
import { matchPath, Navigate } from "react-router-dom";
import { returnsClaimsAnalyticsRoutes } from "../../../analytics/returns-claims/route";
import { returnsProcessingAnalyticsRoutes } from "../../../analytics/returns-processing/route";
import { registrationRoutes } from "../../../registration/route";
import {
  claimRoutes,
  recycleRoutes,
  returnRoutes,
  warrantyRoutes,
} from "../../../return/route";

const reportBreadcrumb = breadcrumbSlot();

const RETURNS_ROUTE_BASE = "/stores/:storeId/returns";

const HIDE_PADDING_ROUTES: string[] = [
  "/returns/*",
  "/registrations/*",
  "/claims/*",
  "/warranties/*",
  "/recycle/*",
  "/analytics/returns-claims",
  "/analytics/returns-claims/*",
  "/analytics/returns-processing",
  "/analytics/returns-processing/*",
  "/analytics/managed-returns",
  "/analytics/reports",
  "/analytics/reports/*",
];

function hidePadding(url: string): boolean {
  return HIDE_PADDING_ROUTES.some(
    (route) => !!matchPath(`${RETURNS_ROUTE_BASE}${route}`, url),
  );
}

const ManagedReturnsAccessGuard = memo(function ManagedReturnsAccessGuard({
  children,
}: {
  children: ReactNode;
}) {
  const team = useRequiredContext(TeamContext);
  const routes = useRequiredContext(MerchantRoutesContext);

  const isManagedReturnsEnabled = team.settings.redoManagedReturns?.enabled;

  if (!isManagedReturnsEnabled) {
    return <Navigate replace to={routes.returnsHref} />;
  }

  return <>{children}</>;
});

export const returnsRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Returns" },
    async lazy() {
      const { Page } = await import("../../page");
      return { element: <Page hidePadding={hidePadding} /> };
    },
    children: [
      { index: true, element: <Navigate replace to="returns" /> },
      {
        path: "warranties",
        children: warrantyRoutes,
        handle: { permission: OrderManagementPermissions.MANAGE_WARRANTY },
      },
      {
        path: "returns",
        children: returnRoutes,
        handle: { permission: OrderManagementPermissions.VIEW_RETURN },
      },
      {
        path: "claims",
        children: claimRoutes,
        handle: {
          permission: [
            OrderManagementPermissions.MANAGE_CARRIER_CLAIM,
            OrderManagementPermissions.MANAGE_PACKAGE_PROTECTION_CLAIM,
          ],
        },
      },
      {
        path: "registrations",
        children: registrationRoutes,
        handle: { permission: OrderManagementPermissions.MANAGE_WARRANTY },
      },
      {
        path: "recycle",
        children: recycleRoutes,
        handle: {
          permission: OrderManagementPermissions.MANAGE_RECYCLING_REQUESTS,
        },
      },
      {
        path: "analytics/returns-claims",
        handle: { breadcrumb: "Analytics", breadcrumbNavigate: false },
        children: returnsClaimsAnalyticsRoutes,
      },
      {
        path: "analytics/returns-processing",
        handle: { breadcrumb: "Analytics", breadcrumbNavigate: false },
        children: returnsProcessingAnalyticsRoutes,
      },
      {
        path: "analytics/managed-returns",
        handle: { breadcrumb: "Analytics", breadcrumbNavigate: false },
        children: [
          {
            index: true,
            handle: { breadcrumb: "Managed Returns" },
            async lazy() {
              const { RedoManagedReturnsPage } = await import(
                "@redotech/redo-merchant-managed-returns-app/redo-managed-returns-page"
              );
              return {
                Component: () => {
                  const team = useRequiredContext(TeamContext);
                  const isReFulfillmentEnabled =
                    team.settings.reFulfillment?.enabled ?? false;
                  return (
                    <ManagedReturnsAccessGuard>
                      <RedoManagedReturnsPage
                        showRefulfillment={isReFulfillmentEnabled}
                      />
                    </ManagedReturnsAccessGuard>
                  );
                },
              };
            },
          },
        ],
      },
      {
        path: "analytics/reports",
        handle: {
          breadcrumb: "Reports",
          permission: OrderManagementPermissions.VIEW_REPORTS,
        },
        children: [
          {
            index: true,
            handle: {
              breadcrumb: reportBreadcrumb,
              permission: OrderManagementPermissions.VIEW_REPORTS,
            },
            async lazy() {
              const { ReportPage } = await import(
                "../../../analytics/reports/report"
              );
              return { element: <ReportPage breadcrumb={reportBreadcrumb} /> };
            },
          },
          {
            path: "custom",
            handle: {
              breadcrumb: "Custom",
              permission: OrderManagementPermissions.VIEW_REPORTS,
            },
            children: [
              { index: true, element: <Navigate replace to="..?tab=custom" /> },
              { path: "*", element: <Navigate replace to="..?tab=custom" /> },
            ],
          },
        ],
      },
    ],
  },
];
